const debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};
export class EdmSlider extends HTMLElement {
  static dotRadius = 6.5;
  static offset = 30;
  constructor() {
    super();
    this.nextBtn = null;
    this.prevBtn = null;
    this.inner = null;
    this.slides = [];
    this.pager = null;
    this.dots = [];
    this.totalPages = 0;
    this.scrollHandler = debounce(this.handleScroll.bind(this), 10);
    this.selfObserver = new IntersectionObserver(this.handleSelfIntersection.bind(this), {
      root: document,
      rootMargin: '0px',
      threshold: 0,
    });
    this.slideObserver = new IntersectionObserver(this.handleIntersection.bind(this), {
      root: this,
      rootMargin: `0px -${EdmSlider.offset}px 0px 0px`,
      threshold: 0,
    });
    window.addEventListener('resize', this.init.bind(this));
  }

  get visibleSlides() {
    return this.slides.filter((slide) => slide.classList.contains('visible'));
  }

  get nextSlide() {
    const slide = this.visibleSlides.at(-1);
    return slide.nextElementSibling || this.slides.at(0);
  }

  get prevSlide() {
    const slide = this.visibleSlides.at(0);
    return slide.previousElementSibling || this.slides.at(-1);
  }

  get currentDot() {
    return this.dots.find((dot) => dot.classList.contains('active'));
  }

  get currentPage() {
    return Math.ceil(this.inner.scrollLeft / this.inner.clientWidth) + 1;
  }

  get stickySlide() {
    return this.slides.find((slide) => slide.classList.contains('sticky'));
  }

  connectedCallback() {
    this.nextBtn = this.querySelector('button.next');
    if (!this.nextBtn) {
      this.nextBtn = document.createElement('button');
      this.nextBtn.classList.add('next');
      this.nextBtn.ariaLabel = 'Next Slide';
      this.appendChild(this.nextBtn);
    }
    this.prevBtn = this.querySelector('button.prev');
    if (!this.prevBtn) {
      this.prevBtn = document.createElement('button');
      this.prevBtn.classList.add('prev');
      this.prevBtn.ariaLabel = 'Previous Slide';
      this.appendChild(this.prevBtn);
    }
    this.inner = this.querySelector('.inner');
    if (!this.inner) {
      this.inner = document.createElement('div');
      this.inner.classList.add('inner');
      this.appendChild(this.inner);
    }
    this.pager = this.querySelector('.pager');
    if (!this.pager) {
      this.pager = document.createElement('div');
      this.pager.classList.add('pager');
      this.appendChild(this.pager);
    }

    this.nextBtn.addEventListener('click', this.next.bind(this));
    this.prevBtn.addEventListener('click', this.prev.bind(this));

    this.init();
  }

  init() {
    this.slides = Array.from(this.inner.querySelectorAll('.slide'));
    this.inner.removeEventListener('scroll', this.scrollHandler);
    this.inner.addEventListener('scroll', this.scrollHandler);
    this.determineScrolling();
    this.renderDots();
    this.observeSlides();
    this.selfObserver.disconnect();
    this.selfObserver.observe(this);
    this.inner.dispatchEvent(new Event('scroll'));
  }

  disconnectedCallback() {
    this.inner.removeEventListener('scroll', this.handleScroll);
    this.slideObserver?.disconnect();
  }

  observeSlides() {
    this.slides.map((slide) => {
      this.slideObserver.observe(slide);
    });
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
      } else {
        entry.target.classList.remove('visible');
      }
    });
  }

  handleSelfIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.determineScrolling();
        this.renderDots();
        this.inner.dispatchEvent(new Event('scroll'));
        this.dispatchEvent(new CustomEvent('visible'));
      } else {
        this.dispatchEvent(new CustomEvent('hidden'));
      }
    });
  }

  selectDot(index, scroll = true) {
    if (this.dots.length === 0) return;
    this.dots.forEach((dot) => {
      dot.classList.remove('active');
    });
    if (index && this.dots[index] === undefined) {
      if (index < 0) {
        index = 0;
      } else if (index >= this.dots.length) {
        index = this.dots.length - 1;
      }
    }
    if (scroll) {
      this.inner.scrollLeft = this.inner.clientWidth * index;
    }
    if (this.dots[index]) {
      this.dots[index].classList.add('active');
    }
  }

  renderDots() {
    if (!this.pager) {
      return;
    }
    this.pager.innerHTML = '';
    if (this.totalPages === 0) {
      return;
    }
    const gap = EdmSlider.dotRadius * 4;
    // Create the SVG namespace
    const svgns = 'http://www.w3.org/2000/svg';

    // Create the <svg> container
    const svg = document.createElementNS(svgns, 'svg');
    svg.setAttribute('width', this.totalPages * gap);
    svg.setAttribute('height', EdmSlider.dotRadius * 2);
    svg.setAttribute('viewBox', `0 0 ${this.totalPages * gap} ${EdmSlider.dotRadius * 2}`);
    svg.setAttribute('fill', 'currentColor');

    // Append the SVG to the body (or any container)
    document.body.appendChild(svg);

    this.dots = Array.from({ length: this.totalPages }, (_, index) => {
      // Create a <circle> element
      const circle = document.createElementNS(svgns, 'circle');
      circle.setAttribute('cx', EdmSlider.dotRadius + index * gap); // center x
      circle.setAttribute('cy', EdmSlider.dotRadius); // center y
      circle.setAttribute('r', EdmSlider.dotRadius); // radius

      circle.addEventListener('click', this.selectDot.bind(this, index));

      // Append the circle to the SVG container
      svg.appendChild(circle);
      return circle;
    });
    this.pager.appendChild(svg);
  }

  determineScrolling() {
    if (this.inner.scrollWidth > this.inner.clientWidth) {
      this.classList.add('scrollable');
      this.totalPages = Math.ceil(this.inner.scrollWidth / this.inner.clientWidth);
    } else {
      this.classList.remove('scrollable');
    }
  }

  stickSlideToEnd() {
    if (!this.stickySlide) return;
    const stickySlide = this.stickySlide;
    const lastVisibleSlide = this.visibleSlides.at(-1);
    const endIndex = this.slides.indexOf(lastVisibleSlide);
    const last = endIndex === this.slides.length - 1;
    this.slides = this.slides.filter((slide) => {
      return slide !== stickySlide && slide !== lastVisibleSlide;
    });
    this.slides = [
      ...this.slides.slice(0, endIndex),
      last ? lastVisibleSlide : stickySlide,
      last ? stickySlide : lastVisibleSlide,
      ...this.slides.slice(endIndex),
    ];
    this.slides.forEach((slide, index) => {
      slide.style.order = index + 1;
    });
  }

  handleScroll() {
    this.stickSlideToEnd();
    if (this.inner.scrollLeft === 0) {
      this.prevBtn.classList.add('hidden');
      this.dispatchEvent(new CustomEvent('reached.start', { bubbles: true }));
    } else {
      this.prevBtn.classList.remove('hidden');
    }
    if (this.inner.scrollLeft + this.inner.clientWidth >= this.inner.scrollWidth) {
      this.nextBtn.classList.add('hidden');
      this.dispatchEvent(new CustomEvent('reached.end', { bubbles: true }));
    } else {
      this.nextBtn.classList.remove('hidden');
    }

    this.selectDot(this.currentPage - 1, false);
  }

  next() {
    if (this.pager) {
      this.selectDot(this.dots.indexOf(this.currentDot) + 1);
    } else {
      this.nextSlide.scrollIntoView({
        behavior: 'smooth',
        inline: 'end',
        block: 'nearest',
      });
    }
  }

  prev() {
    if (this.pager) {
      this.selectDot(this.dots.indexOf(this.currentDot) - 1);
    } else {
      this.prevSlide.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      });
    }
  }
}
window.EdmSlider = EdmSlider;
// if (customElements.get('edm-slider') === undefined) {
//   customElements.define('edm-slider', EdmSlider);
// }
